// Radio options rendered as cards.
//
// This is shadcn's documented "choice cards" composition — Field in horizontal
// orientation wrapping a RadioGroupItem — and the reason to prefer it over bare
// radios is that a small circle beside a paragraph makes the selection feel
// uncertain: the whole card should be the target.
//
// Both were private to the TLS controls. They are the console's choice-selection
// idiom, so they live here and are used wherever a decision has more context than
// a label.

import {
	Field,
	FieldContent,
	FieldDescription,
	FieldLabel,
	FieldTitle,
} from "@veil/ui/components/field";
import { RadioGroupItem } from "@veil/ui/components/radio-group";
import { cn } from "@veil/ui/lib/utils";
import type { ComponentType, ReactNode } from "react";

export interface ChoiceCardProps {
	id: string;
	value: string;
	disabled?: boolean;
	icon?: ComponentType<{ className?: string }>;
	title: ReactNode;
	description: ReactNode;
	/**
	 * Why this option is unavailable. Rendered as help text and wired to the
	 * radio via aria-describedby, so a disabled choice explains itself to screen
	 * readers instead of relying on a hover-only title.
	 */
	unavailableReason?: ReactNode;
	className?: string;
}

/** Bordered radio option: radio + optional icon + title + one-liner. */
export function ChoiceCard({
	id,
	value,
	disabled,
	icon: Icon,
	title,
	description,
	unavailableReason,
	className,
}: ChoiceCardProps) {
	const reasonId = unavailableReason ? `${id}-reason` : undefined;
	return (
		<FieldLabel
			htmlFor={id}
			className={cn(disabled && "opacity-55", className)}
		>
			<Field orientation="horizontal" data-disabled={disabled || undefined}>
				<RadioGroupItem
					value={value}
					id={id}
					disabled={disabled}
					aria-describedby={reasonId}
				/>
				{Icon ? (
					<Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
				) : null}
				<FieldContent>
					<FieldTitle>{title}</FieldTitle>
					<FieldDescription>{description}</FieldDescription>
					{unavailableReason ? (
						<FieldDescription id={reasonId} className="italic">
							{unavailableReason}
						</FieldDescription>
					) : null}
				</FieldContent>
			</Field>
		</FieldLabel>
	);
}

/** Borderless radio row for sub-choices inside a branch panel. */
export function InlineChoice({
	id,
	value,
	disabled,
	title,
	description,
}: {
	id: string;
	value: string;
	disabled?: boolean;
	title: ReactNode;
	description: ReactNode;
}) {
	return (
		<Field
			orientation="horizontal"
			data-disabled={disabled || undefined}
			className="rounded-md px-2 py-2 transition-colors hover:bg-muted/50"
		>
			<RadioGroupItem value={value} id={id} disabled={disabled} />
			<FieldContent>
				<FieldLabel htmlFor={id}>{title}</FieldLabel>
				<FieldDescription>{description}</FieldDescription>
			</FieldContent>
		</Field>
	);
}
